import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

import { statusIcons } from "@/components/ui/badge"

const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-md border px-3 py-2.5 text-ui has-[>svg]:grid-cols-[1rem_1fr] has-[>svg]:gap-x-2.5 [&:has(>svg:not([data-slot=alert-icon]))>[data-slot=alert-icon]]:hidden [&>svg]:size-4 [&>svg]:translate-y-px [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "border-border bg-card text-card-foreground",
        info: "border-info-border bg-info-bg text-foreground *:data-[slot=alert-title]:text-info [&>svg]:text-info",
        success:
          "border-success-border bg-success-bg text-foreground *:data-[slot=alert-title]:text-success [&>svg]:text-success",
        warning:
          "border-warning-border bg-warning-bg text-foreground *:data-[slot=alert-title]:text-warning [&>svg]:text-warning",
        destructive:
          "border-destructive-border bg-destructive-bg text-foreground *:data-[slot=alert-title]:text-destructive [&>svg]:text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Alert({
  className,
  variant,
  icon,
  role,
  children,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof alertVariants> & {
    icon?: React.ReactNode
  }) {
  const resolved = variant ?? "default"
  const DefaultIcon = resolved === "default" ? null : statusIcons[resolved]
  const urgent = resolved === "destructive" || resolved === "warning"
  // A neutral callout is static content, so it is not a live region.
  const liveRole = resolved === "default" ? undefined : "status"

  return (
    <div
      data-slot="alert"
      data-variant={resolved}
      role={role ?? (urgent ? "alert" : liveRole)}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon === undefined && DefaultIcon ? (
        <DefaultIcon data-slot="alert-icon" aria-hidden="true" />
      ) : (
        icon
      )}
      {children}
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn("col-start-2 min-h-4 font-medium", className)}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-ui [&_a]:underline [&_a]:underline-offset-4 [&_a]:focus-ring [[data-variant=default]>&]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn(
        "col-start-2 mt-2 flex flex-wrap items-center gap-2",
        className,
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction, alertVariants }
